import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "./store"
import type { DriverNameOrder } from "@/lib/driverNameOrder"

export type RoutingProvider = "here" | "ptv" | "tomtom"

export interface GpsServiceConfig {
  routingProvider: RoutingProvider
}

/** Vhost-wide identity-display settings from `urn:slickshift:account-config:0` item "identity". */
export interface IdentityConfig {
  /** When true the sender's phone number is appended to their name in message headers. */
  showPhoneWithName: boolean
  /** WhatsApp number currently in use for this vhost. Absent when not configured. */
  whatsappNumber?: string
}

export interface ServiceConfigState {
  gps?: GpsServiceConfig
  identity?: IdentityConfig
  /** Vhost-wide, from account_config's store; see `@/lib/driverNameOrder`. */
  driverNameOrder?: DriverNameOrder
}

const initialState: ServiceConfigState = {}

const serviceConfigSlice = createSlice({
  name: "serviceConfig",
  initialState,
  reducers: {
    setGpsServiceConfig(state, action: PayloadAction<GpsServiceConfig>) {
      state.gps = action.payload
    },
    clearGpsServiceConfig(state) {
      state.gps = undefined
    },
    setIdentityConfig(state, action: PayloadAction<IdentityConfig>) {
      state.identity = action.payload
    },
    clearIdentityConfig(state) {
      state.identity = undefined
    },
    setDriverNameOrder(state, action: PayloadAction<DriverNameOrder>) {
      state.driverNameOrder = action.payload
    },
  },
})

export const {
  setGpsServiceConfig,
  clearGpsServiceConfig,
  setIdentityConfig,
  clearIdentityConfig,
  setDriverNameOrder,
} = serviceConfigSlice.actions

/** True when the vhost is configured to show a sender's phone number next to their name. */
export const selectWhatsappNumber = (state: RootState): string | undefined =>
  state.serviceConfig.identity?.whatsappNumber

export const selectShowPhoneWithName = (state: RootState): boolean =>
  state.serviceConfig.identity?.showPhoneWithName ?? false

export const selectDriverNameOrder = (state: RootState): DriverNameOrder =>
  state.serviceConfig.driverNameOrder ?? "first_last"

export default serviceConfigSlice.reducer
